import React, { type FC, type ReactNode, type PropsWithChildren } from 'react';

import { getColor } from '@lucid/theme';

import type { Lucid } from '../../../types/types';
import Box from '../../box/box';
import Container from '../../container/container';
import Heading from '../../heading/heading';
import Link from '../../link/link';
import Paper from '../../paper/paper';

interface WithChildrenProps {
  /** Allows to pass in a Beacon event to the CTA link */
  beaconEvent?: never;
  /** The children rendered inside the SimpleCta component */
  children?: ReactNode;
  /** The cta url & text. */
  cta?: never;
}

interface WithCtaProps {
  /** Allows to pass in a Beacon event to the CTA link */
  beaconEvent?: Lucid.Beacon.Event;
  /** The children rendered inside the SimpleCta component */
  children?: never;
  /** The cta url & text. */
  cta?: Lucid.Directus.Generated.Links;
}

interface AllSimpleCtaProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Allows  to pass extra padding to inner Container */
  extraPadding?: number;
  /** The text displayed in the card, next to the button. */
  heading: string;
  /** Sets the top half of the background to transparent */
  overlapColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
}

export type SimpleCtaProps = (WithChildrenProps | WithCtaProps) & AllSimpleCtaProps;

const SimpleCta: FC<PropsWithChildren<SimpleCtaProps>> = ({
  beaconEvent,
  children,
  className,
  cta,
  extraPadding,
  heading,
  overlapColor = 'transparent',
}) => (
  <Box
    css={theme => ({
      label: 'simpleCTA',
      background: `linear-gradient(${getColor({
        color: overlapColor,
        theme,
      })} 50%, transparent 50%)`,
      padding: `${theme.space.s104}px 0`,
    })}
    // eslint-disable-next-line @stylistic/jsx-sort-props
    className={className}
  >
    <Container css={{ display: 'flex' }} extraPadding={extraPadding}>
      <Paper
        css={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Heading
          as="h2"
          css={theme => ({
            margin: theme.space.s0,
            flex: '0 1 100%',
            [theme.breakpoints.bp768.min]: {
              marginRight: theme.space.s80,
              flex: '1 1',
            },
          })}
          id="simple-cta-heading"
          variant="headingXL"
        >
          {heading}
        </Heading>
        <Box
          css={theme => ({
            display: 'flex',
            flex: '0 1 100%',
            alignSelf: 'center',
            justifyContent: 'center',
            marginTop: '2rem',
            [theme.breakpoints.bp768.min]: {
              flex: '0 1',
              marginTop: 0,
            },
          })}
        >
          {cta ? (
            <Link
              displayAsButton
              aria-describedby="simple-cta-heading"
              beaconEvent={beaconEvent}
              css={theme => ({
                flex: '1 1 100%',
                [theme.breakpoints.bp500.min]: {
                  flex: '0 1 auto',
                },
              })}
              href={cta?.final_url}
            >
              {cta?.default_link_text}
            </Link>
          ) : (
            children
          )}
        </Box>
      </Paper>
    </Container>
  </Box>
);

export { SimpleCta };
export default SimpleCta;
